import React from 'react'
import { Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { IMovementRS, TStorageItemRS } from '../../../../models/storageModel'
import { getProductRotation, TProductRotationRow } from '../../../../utils/reportUtils'
import { TExcelColumn } from '../../../../utils/excelExport'
import { textNumberFormater } from '../../../../utils'

const { Text } = Typography

interface ProductRotationReportProps {
  items: TStorageItemRS[]
  movements: IMovementRS[]
  loading?: boolean
}

export const rotationExcelColumns: TExcelColumn[] = [
  { header: 'Código', key: 'code', width: 18 },
  { header: 'Producto', key: 'name', width: 30 },
  { header: 'Existencia Actual', key: 'currentStock', width: 16, format: 'number' },
  { header: 'Ingresos (período)', key: 'entryQuantity', width: 16, format: 'number' },
  { header: 'Salidas/Ventas (período)', key: 'exitQuantity', width: 18, format: 'number' },
  { header: 'Índice de Rotación', key: 'rotationIndex', width: 16, format: 'number' },
  { header: 'Días Est. de Cobertura', key: 'estimatedDays', width: 18, format: 'number' }
]

export function getRotationExcelData(items: TStorageItemRS[], movements: IMovementRS[]) {
  return getProductRotation(items, movements)
}

export const ProductRotationReport: React.FC<ProductRotationReportProps> = ({
  items,
  movements,
  loading = false
}) => {
  const rotationData = getProductRotation(items, movements)

  const columns: ColumnsType<TProductRotationRow> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Código',
      dataIndex: 'code',
      key: 'code',
      width: 130,
      render: (text) => <code>{text || '-'}</code>
    },
    {
      title: 'Producto',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Existencia Actual',
      dataIndex: 'currentStock',
      key: 'currentStock',
      width: 130,
      align: 'right',
      render: (val) => textNumberFormater(val)
    },
    {
      title: 'Ingresos (Período)',
      dataIndex: 'entryQuantity',
      key: 'entryQuantity',
      width: 140,
      align: 'right',
      render: (val) => <Text style={{ color: '#52c41a' }}>{textNumberFormater(val)}</Text>
    },
    {
      title: 'Salidas (Período)',
      dataIndex: 'exitQuantity',
      key: 'exitQuantity',
      width: 140,
      align: 'right',
      sorter: (a, b) => a.exitQuantity - b.exitQuantity,
      render: (val) => <Text style={{ color: '#ff4d4f' }}>{textNumberFormater(val)}</Text>
    },
    {
      title: 'Índice Rotación',
      dataIndex: 'rotationIndex',
      key: 'rotationIndex',
      width: 130,
      align: 'right',
      sorter: (a, b) => a.rotationIndex - b.rotationIndex,
      render: (val) => <Text strong style={{ color: '#1890ff' }}>{val}</Text>
    },
    {
      title: 'Días Cobertura Est.',
      dataIndex: 'estimatedDays',
      key: 'estimatedDays',
      width: 140,
      align: 'right',
      sorter: (a, b) => a.estimatedDays - b.estimatedDays,
      render: (days) => {
        if (days >= 999) return <Tag color="default">Sin Salidas</Tag>
        let color = 'green'
        if (days > 60) color = 'orange'
        if (days > 120) color = 'red'
        return <Tag color={color}>{days} días</Tag>
      }
    }
  ]

  return (
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <Table<TProductRotationRow>
        columns={columns}
        dataSource={rotationData}
        loading={loading}
        pagination={{
          defaultPageSize: 15,
          showSizeChanger: true,
          pageSizeOptions: ['15', '30', '50', '100'],
          showTotal: (total) => `Total ${total} productos evaluados`
        }}
        size="small"
        bordered
        scroll={{ x: 'max-content' }}
      />
    </div>
  )
}

export default ProductRotationReport
